import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config';

const SessionContext = createContext();

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export const SessionProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionTimeout, setSessionTimeout] = useState(null);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const lastActivityRef = useRef(Date.now());
  const sessionTimeoutRef = useRef(null);

  // Session timeout duration (2 hours in milliseconds)
  const SESSION_TIMEOUT_DURATION = 2 * 60 * 60 * 1000; // 2 hours
  const REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    setLastActivity(now);
  }, []);

  // Check if session is expired
  const isSessionExpired = useCallback(() => {
    return Date.now() - lastActivityRef.current > SESSION_TIMEOUT_DURATION;
  }, []);

  // Refresh session
  const refreshSession = useCallback(async () => {
    try {
      const response = await axios.post(getApiUrl('/session/refresh'), {}, {
        withCredentials: true
      });
      
      if (response.data.user) {
        setUser(response.data.user);
        // Don't call updateActivity here to avoid infinite loops
        return true;
      }
    } catch (error) {
      console.log('Session refresh failed:', error);
      return false;
    }
  }, []);

  // Logout user
  const logout = useCallback(async () => {
    try {
      await axios.post(getApiUrl('/logout'), {}, {
        withCredentials: true
      });
    } catch (error) {
      console.log('Logout error:', error);
    } finally {
      setUser(null);
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
        sessionTimeoutRef.current = null;
      }
      setSessionTimeout(null);
      const now = Date.now();
      lastActivityRef.current = now;
      setLastActivity(now);
    }
  }, []);

  // Check authentication status
  const checkAuth = useCallback(async (retryCount = 0) => {
    try {
      const response = await axios.get(getApiUrl('/user/profile'), {
        withCredentials: true
      });
      
      if (response.data.user) {
        setUser(response.data.user);
        // Don't call updateActivity here to avoid infinite loops
        return true;
      }
    } catch (error) {
      console.log('Auth check failed:', error);
      
      // Retry up to 3 times with exponential backoff
      if (retryCount < 3) {
        const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        console.log(`Retrying session auth check in ${delay}ms (attempt ${retryCount + 1}/3)`);
        setTimeout(() => checkAuth(retryCount + 1), delay);
        return false;
      }
      
      setUser(null);
      return false;
    }
  }, []);

  // Handle session timeout
  const handleSessionTimeout = useCallback(() => {
    console.log('Session expired due to inactivity');
    setUser(null);
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
    setSessionTimeout(null);
    
    // Show notification to user
    if (window.confirm('Your session has expired due to inactivity. Please log in again.')) {
      window.location.href = '/';
    }
  }, []);

  // Set up activity listeners
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      updateActivity();
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, []);

  // Set up session timeout
  useEffect(() => {
    if (user) {
      // Clear existing timeout
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }

      // Set new timeout
      const timeout = setTimeout(() => {
        if (isSessionExpired()) {
          handleSessionTimeout();
        }
      }, SESSION_TIMEOUT_DURATION);

      sessionTimeoutRef.current = timeout;
      setSessionTimeout(timeout);

      return () => {
        if (timeout) {
          clearTimeout(timeout);
        }
      };
    }
  }, [user, isSessionExpired, handleSessionTimeout]);

  // Set up periodic session refresh
  useEffect(() => {
    if (user) {
      const refreshInterval = setInterval(async () => {
        const success = await refreshSession();
        if (!success) {
          handleSessionTimeout();
        }
      }, REFRESH_INTERVAL);

      return () => {
        clearInterval(refreshInterval);
      };
    }
  }, [user]);

  // Initial auth check
  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);
      await checkAuth();
      setIsLoading(false);
    };

    // Add a small delay to prevent race conditions with other auth checks
    setTimeout(initializeAuth, 1000);
  }, []);

  const value = {
    user,
    setUser,
    isLoading,
    checkAuth,
    logout,
    refreshSession,
    updateActivity,
    isSessionExpired
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}; 